import { motion } from "framer-motion";
import { MapPin, Monitor, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WHATSAPP, buildWhatsappLink } from "@/lib/config";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Hero = () => {
  return (
    <section
      id="inicio"
      className="relative overflow-hidden bg-[#FDFBF7] pt-28 pb-20 md:pt-36 md:pb-32"
      data-testid="hero-section"
    >
      {/* Decorative gold dot pattern */}
      <div className="pointer-events-none absolute top-0 right-0 h-[400px] w-[400px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[300px] w-[300px] rounded-full bg-[#3E2723]/5 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:px-12 lg:grid-cols-2 lg:gap-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="order-2 lg:order-1"
        >
          <motion.div
            variants={fadeUp}
            custom={0}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-[#8B6F1F]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Dra. Mariana Alves
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="font-serif text-4xl leading-[1.1] tracking-tight text-[#3E2723] sm:text-5xl lg:text-6xl"
            data-testid="hero-headline"
          >
            Seu corpo merece <br />
            <em className="font-medium italic text-[#8B6F1F]">
              mais do que dietas.
            </em>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-6 max-w-xl text-base text-[#4E342E]/80 md:text-lg"
          >
            Recupere sua energia, sua confiança e sua saúde através de um
            acompanhamento médico individualizado.
          </motion.p>

          <motion.p
            variants={fadeUp}
            custom={3}
            className="mt-4 max-w-xl font-serif text-lg italic text-[#8B6F1F] md:text-xl"
          >
            “O seu futuro é magro — e ele começa agora.”
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={4}
            className="mt-10 space-y-3"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#795548]">
              Escolha onde deseja ser atendida
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button
                asChild
                className="rounded-full bg-[#3E2723] px-7 py-6 text-xs uppercase tracking-[0.2em] text-white hover:bg-[#5D4037]"
                data-testid="hero-cta-mogi"
              >
                <a
                  href={buildWhatsappLink(WHATSAPP.mogi_guacu)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  Mogi Guaçu
                </a>
              </Button>
              <Button
                asChild
                className="rounded-full bg-[#D4AF37] px-7 py-6 text-xs uppercase tracking-[0.2em] text-white hover:bg-[#C5A059]"
                data-testid="hero-cta-artur"
              >
                <a
                  href={buildWhatsappLink(WHATSAPP.artur_nogueira)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  Artur Nogueira
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="rounded-full border-[#3E2723] px-7 py-6 text-xs uppercase tracking-[0.2em] text-[#3E2723] hover:bg-[#3E2723] hover:text-white"
                data-testid="hero-cta-online"
              >
                <a
                  href={buildWhatsappLink(WHATSAPP.online)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Monitor className="mr-2 h-4 w-4" />
                  Online
                </a>
              </Button>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={5}
            className="mt-10 flex flex-col gap-2 text-sm text-[#795548] sm:flex-row sm:gap-6"
          >
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#D4AF37]" />
              Mogi Guaçu &amp; Artur Nogueira
            </span>
            <span className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-[#D4AF37]" />
              Online para todo o Brasil
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="order-1 lg:order-2"
        >
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-[#D4AF37]/20 to-transparent" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_metodo-mariana/artifacts/xkfa87b8_b1bc2f4d-42d7-4c02-8dbb-086be66f23ff.jpg"
              alt="Dra. Mariana Alves - médica especialista em saúde da mulher"
              className="relative h-[500px] w-full rounded-[2rem] object-cover shadow-2xl md:h-[640px]"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl bg-white px-6 py-4 shadow-xl md:block">
              <p className="font-serif text-2xl text-[#3E2723]">9+</p>
              <p className="text-xs uppercase tracking-widest text-[#795548]">
                anos de medicina
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
